import gameSchedule from '@/data/gameSchedule'
import { getDayOfTheYear } from '../../utils/dateHelper'
import dynamic from 'next/dynamic'
import animerdleList from '@/data/animerdle_list.json'
import { SHA1 } from '../../utils/encryption'
import mailgo from 'mailgo'
import { useTheme } from 'next-themes'
import CurrentBar from '@/components/guess-bars/CurrentBar'
import FailedBar from '@/components/guess-bars/FailedBar'
import SkipBar from '@/components/guess-bars/SkipBar'
import { PageSEO } from '@/components/SEO'
import siteMetadata from '@/data/siteMetadata'
import SectionContainer from '@/components/SectionContainer'
import Image from 'next/image'
import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/router'

const DynamicPlayController = dynamic(() => import('@/components/PlayController'))

export default function ClassicPage({ totalStats }) {
  const router = useRouter()
  const { id } = router.query

  const animeList = animerdleList

  const [todayAnime, setTodayAnime] = useState(null)
  const [soundUrl, setSoundUrl] = useState('')

  let schedule = null
  const updateThisAnime = (id) => {
    schedule = gameSchedule[id]

    if (schedule === undefined) {
      return
    }

    let additionals = ''
    if (schedule.id !== undefined) {
      additionals = `_audio${schedule.audioNum}`
      schedule = schedule.id
    }

    const _todayAnime = animeList.find((x) => x.id === schedule)
    const audioName = `${_todayAnime.id.toString()}`

    setTodayAnime(_todayAnime)
    setSoundUrl(`../static/assets/anime/${SHA1(audioName)}${additionals}.mp3`)
    setIsDataReady(true)
  }

  const [currentGameStats, setCurrentGameStats] = useState({
    day: getDayOfTheYear(),
    malId: gameSchedule[getDayOfTheYear()],
    actions: [],
  })

  const resetCurrentGameStats = () => {
    setCurrentGameStats({
      day: getDayOfTheYear(),
      malId: gameSchedule[getDayOfTheYear()],
      actions: [],
    })
  }

  const [currentAttemptNum, setCurrentAttemptNum] = useState(0)
  const [action, setAction] = useState([...Array('', '', '', '', '', '')])
  const [submittedAnime, setSubmittedAnime] = useState([])

  for (let i = 0; i < currentGameStats.actions.length; i++) {
    if (currentGameStats.actions[i].skip) {
      action[i] = 'skip'
    }
    if (currentGameStats.actions[i].failedGuess) {
      const anime = animeList.find((x) => x.id === currentGameStats.actions[i].animeId)
      submittedAnime[i] = anime.title
      action[i] = 'failedguess'
    }
    if (currentGameStats.actions[i].win) {
      const anime = animeList.find((x) => x.id === currentGameStats.actions[i].animeId)
      submittedAnime[i] = anime.title
      action[i] = 'correctguess'
    }
  }

  const [isDataReady, setIsDataReady] = useState(false)

  const mailgoConfig = {
    dark: true,
  }

  useEffect(() => {
    if (router.isReady && isNaN(Number(id))) {
      router.push('/')
    }
    if (router.isReady) {
      updateThisAnime(id)
      if (schedule === undefined) {
        router.push('/')
      }
    }
    mailgo(mailgoConfig)
  }, [router.isReady])

  const [win, setWin] = useState(false)
  const [finishedGame, setFinishedGame] = useState(false)
  const finishedGameStateRef = useRef(finishedGame)
  const setFinishedGameState = (data) => {
    finishedGameStateRef.current = data
    setFinishedGame(data)
  }

  const [opaqueExceptPlay, setOpaqueExceptPlay] = useState(false)

  const theme = useTheme()

  const resetToInitialState = () => {
    const temp = [...Array('', '', '', '', '', '')]
    setAction(temp)
    setCurrentAttemptNum(0)
    setSubmittedAnime([])
    resetCurrentGameStats()
    setFinishedGameState(false)
  }

  const onSubmit = (anime) => {
    if (todayAnime.id === anime.id) {
      // player won, show him finishedGame screen
      const temp = action
      temp[currentAttemptNum] = 'correctguess'
      setAction(temp)
      setFinishedGameState(true)
      setWin(true)
    } else {
      let temp = submittedAnime
      temp[currentAttemptNum] = anime.title
      setSubmittedAnime(temp)

      temp = action
      temp[currentAttemptNum] = 'failedguess'
      setAction(temp)

      updateAttempt()

      if (currentAttemptNum === 5) {
        setFinishedGameState(true)
      }
    }
  }

  const onSkip = () => {
    let temp = action
    temp[currentAttemptNum] = 'skip'
    setAction(temp)
    updateAttempt()

    if (currentAttemptNum === 5) {
      setFinishedGameState(true)
    }
  }

  const updateAttempt = () => {
    setCurrentAttemptNum(currentAttemptNum + 1)
  }

  // Render Guess Bars

  const renderResultBar = (barNum) => {
    if (!action[barNum]) {
      return <CurrentBar isSelected={currentAttemptNum === barNum} />
    } else {
      if (action[barNum] === 'failedguess') {
        return <FailedBar failedGuess={submittedAnime[barNum]} />
      }
      if (action[barNum] === 'skip') {
        return <SkipBar />
      }
    }
  }

  return (
    <>
      <PageSEO
        title={siteMetadata.shortTitle + ' - ' + 'Classic ' + id}
        description={siteMetadata.description}
        keywords={'anime guessing game, animerdle'}
      ></PageSEO>
      <SectionContainer>
        <div className="flex h-full flex-col justify-between">
          {isDataReady && (
            <>
              {finishedGame ? (
                <main className="flex h-full flex-col justify-between">
                  <button aria-label="Go to today's anime guess site">
                    <a href="" className="text-left">
                      <div className="flex min-h-[9rem]">
                        <Image
                          className="rounded-l"
                          src={todayAnime.image_url}
                          alt="Today's Animerdle cover picture"
                          title="Today's Animerdle cover picture"
                          width={160}
                          height={200}
                        />
                        <div className="w-full rounded-md border-gray-700 bg-gray-300 p-5 dark:bg-gray-800">
                          <div className="flex h-full flex-col justify-between">
                            <div className="text-xl font-bold text-gray-800 dark:text-gray-200">
                              {todayAnime.title}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Type: {todayAnime.type}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Status: {todayAnime.status}
                            </p>
                          </div>
                        </div>
                      </div>
                    </a>
                  </button>

                  <div className="flex flex-col gap-3 mb-4">
                    <span className="flex justify-center font-bold">
                      {win && 'You guessed the Animerdle!'}
                      {!win && "You didn't guess this Animerdle."}
                    </span>
                    <div className="flex justify-center text-sm text-gray-400">
                      {win && 'Good job, you know your Anime.'}
                      {!win && 'Better luck next time!'}
                    </div>
                  </div>
                </main>
              ) : (
                <main className="flex h-full flex-col justify-between">
                  <div className="relative flex flex-col justify-between">
                    <div className="grid grid-cols-1 gap-2">
                      {renderResultBar(0)}
                      {renderResultBar(1)}
                      {renderResultBar(2)}
                      {renderResultBar(3)}
                      {renderResultBar(4)}
                      {renderResultBar(5)}
                    </div>
                  </div>
                </main>
              )}
              <div
                className={`${
                  opaqueExceptPlay && 'absolute top-0 left-0 h-full w-full bg-black opacity-70'
                }`}
              ></div>
              <DynamicPlayController
                onSubmit={onSubmit}
                onSkip={onSkip}
                currentAttemptNum={currentAttemptNum}
                setCurrentAttemptNum={setCurrentAttemptNum}
                updateAttempt={() => updateAttempt()}
                finishedGameStateRef={finishedGameStateRef}
                todayAnime={todayAnime}
                setOpaqueExceptPlay={setOpaqueExceptPlay}
                opaqueExceptPlay={opaqueExceptPlay}
                currentGameStats={currentGameStats}
                soundUrl={soundUrl}
              />
            </>
          )}
        </div>
      </SectionContainer>
      <p>{id}</p>
    </>
  )
}
